<template>
  <autocomplete-field
    v-model="item"
    v-model:search="search"
    return-object
    no-filter
    autocomplete="false"
    :hide-no-data="hideNoData"
    :loading="loading"
    :item-title="v => itemTitle(v, searchArgs || {})"
    :items="items"
    :no-data-text="loading ? 'Searching...' : 'No Data'"
    v-bind="$attrs"
  >
    <template v-if="$slots.item" #item="slotProps">
      <slot name="item" v-bind="slotProps" />
    </template>
    <template v-if="$slots['no-data']" #no-data>
      <slot name="no-data" />
    </template>
    <template v-if="$slots.label" #label>
      <slot name="label" />
    </template>
    <template v-if="$slots.message" #message="slotProps">
      <slot name="message" v-bind="slotProps" />
    </template>
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>
    <template
      v-if="$slots['append-inner'] || activeSaveController"
      #append-inner
    >
      <slot name="append-inner" />
      <active-save-indicator
        v-if="activeSaveController"
        :controller="activeSaveController"
      />
    </template>
    <template v-if="$slots.append" #append>
      <slot name="append" />
    </template>
    <template v-if="$slots['prepend-inner']" #prepend-inner>
      <slot name="prepend-inner" />
    </template>
  </autocomplete-field>
</template>

<script setup>
import ActiveSaveIndicator from "@/components/shared/active-save/ActiveSaveIndicator.vue";
import { useSnackbarStore } from "@/stores/snackbar";
import { parseErrorMessage } from "@/util/helpers";
import { ref, toRef, watch } from "vue";
import { getHttpClient } from "@/http-client";

const props = defineProps({
  hideNoData: { type: Boolean, default: true },
  modelValue: {
    type: Object,
    required: false,
    default: () => {}
  },
  composable: {
    type: Function,
    required: true
  },
  searchArgs: {
    type: Object,
    required: false,
    default: null
  },
  activeSaveController: {
    type: Object,
    required: false,
    default: null
  }
});

const propModelValue = toRef(props, "modelValue");
const emit = defineEmits(["update:model-value"]);
const snackbar = useSnackbarStore();

const items = ref([]);
const item = ref(null);
const search = ref("");
const loading = ref(false);

const { comparisonValue, fetchResults, itemTitle } = props.composable();

let timer;
function debounceAndSearch() {
  if (timer) clearTimeout(timer);
  timer = setTimeout(fetchItems, 200);
}

let cancelToken = getHttpClient().CancelToken.source();
async function fetchItems() {
  if (!search.value) return;
  if (
    items.value.some(
      v => search.value === itemTitle(v, props.searchArgs || {})
    ) &&
    items.value.length === 1
  ) {
    return;
  }

  loading.value = true;
  try {
    const result = await fetchResults(
      search.value,
      props.searchArgs || {},
      cancelToken
    );
    if (!result) return;

    items.value.splice(0, items.value.length);
    items.value.push(...result);
  } catch (e) {
    snackbar.showErrorSnackbar({ message: parseErrorMessage(e) });
  } finally {
    loading.value = false;
  }
}

watch(
  propModelValue,
  v => {
    items.value.splice(0, items.value.length);
    if (v && comparisonValue(v)) {
      item.value = v;
      items.value.push(v);
    } else {
      item.value = null;
      search.value = "";
    }
  },
  { immediate: true }
);

watch(item, v => {
  emit("update:model-value", v);
});

watch(search, debounceAndSearch);
</script>
